/**
 * Display-time sanitizer for Quill / Promang rich text.
 * Strips script/iframe/handlers and javascript:/data: (non-image) URLs.
 * Keeps raster data:image (png/jpeg/gif/webp/bmp); svg+xml is stripped.
 */
const DANGEROUS_TAGS =
  "script, iframe, object, embed, form, link, meta, base, svg, math, style";

const URL_ATTRS = new Set([
  "href",
  "src",
  "xlink:href",
  "action",
  "formaction",
  "poster",
  "cite",
  "background",
]);

function compactForProtocol(value) {
  // eslint-disable-next-line no-control-regex -- Strip control characters that can disguise unsafe URL protocols.
  return String(value || "").replace(/[\u0000-\u0020\u007f]/g, "");
}

function isDangerousUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return false;
  const compact = compactForProtocol(raw);
  if (/^(javascript|vbscript|livescript|mhtml):/i.test(compact)) return true;
  if (/^data:/i.test(compact) && !/^data:image\/(png|jpe?g|gif|webp|bmp)/i.test(compact)) {
    return true;
  }
  return false;
}

function styleLooksDangerous(value) {
  const compact = compactForProtocol(value).toLowerCase();
  return (
    compact.includes("javascript:") ||
    compact.includes("expression(") ||
    compact.includes("url(") ||
    compact.includes("-moz-binding")
  );
}

function stripDangerousAttributes(el) {
  [...el.attributes].forEach((attr) => {
    const name = attr.name.toLowerCase();
    if (name.startsWith("on") || name === "srcdoc") {
      el.removeAttribute(attr.name);
      return;
    }
    if (name === "style" && styleLooksDangerous(attr.value)) {
      el.removeAttribute(attr.name);
      return;
    }
    if (URL_ATTRS.has(name) && isDangerousUrl(attr.value)) {
      el.removeAttribute(attr.name);
    }
  });
}

/** http(s) only — used for product links in customer/print preview. */
export function safeHttpUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (!/^https?:/i.test(compactForProtocol(raw))) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
}

export function sanitizeRichHtml(html) {
  const raw = html == null ? "" : String(html);
  if (!raw.trim()) return "";

  if (typeof document === "undefined") {
    return raw
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, "")
      .replace(/<svg[\s\S]*?>[\s\S]*?<\/svg>/gi, "")
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
      .replace(/<form[\s\S]*?>[\s\S]*?<\/form>/gi, "")
      .replace(/<link\b[^>]*>/gi, "")
      .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
      .replace(/\sstyle\s*=\s*(['"])[\s\S]*?\1/gi, "")
      .replace(
        /\s(?:href|src|xlink:href|action|formaction)\s*=\s*(['"])\s*(?:javascript|vbscript|livescript|data):[\s\S]*?\1/gi,
        ""
      );
  }

  const template = document.createElement("template");
  template.innerHTML = raw;
  template.content.querySelectorAll(DANGEROUS_TAGS).forEach((el) => el.remove());
  template.content.querySelectorAll("*").forEach(stripDangerousAttributes);
  return template.innerHTML;
}
